'use client';

import { useMemo, useEffect, useCallback } from 'react';

import { useSetState } from 'src/hooks/use-set-state';

import axios, { endpoints } from 'src/utils/axios';
import { apiEndpoints } from 'src/utils/api-endpoints/index';
import tairraAxiosInstance from 'src/utils/axios-tairra';
import { log } from 'src/utils/console-logger';

import { STORAGE_KEY, TAIRRA_STORAGE_KEY, EMPLOYEE_ID_KEY } from './constants';
import { AuthContext } from '../auth-context';
import { setSession, setTairraApiSession, isValidToken } from './utils';
// ----------------------------------------------------------------------

//TODO-MARK: REMOVE USER CLEANUP
export function AuthProvider({ children }) {
  const { state, setState } = useSetState({
    user: null,
    tairraUser: null,
    tairraUserRights: null,
    loading: true,
    tairraLoading: true,
    accessToken: null, // Add accessToken to the state
  });

  const checkUserSession = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);
      const tairraToken = localStorage.getItem(TAIRRA_STORAGE_KEY);
      const employeeId = localStorage.getItem(EMPLOYEE_ID_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const res = await axios.get(endpoints.auth.me);

        const { user } = res.data;

        setState({ user: { ...user, accessToken }, loading: false });
      } else {
        setState({ user: null, loading: false });
      }

      if (tairraToken && isValidToken(tairraToken)) {
        if (employeeId) {
          const url = `${apiEndpoints.employee.getById}/${employeeId}`;
          tairraAxiosInstance.defaults.headers.common.Authorization = `Bearer ${tairraToken}`;
          const resTairra = await tairraAxiosInstance.get(url);

          if (resTairra.data && resTairra.data.data) {
            setTairraApiSession(tairraToken); // Setting the Tairra API session

            const userRightsUrl = `${apiEndpoints.teams.getCurrentUserRightsById}`;
            const resUserRights = await tairraAxiosInstance.get(userRightsUrl);

            if (resUserRights.data && resUserRights.data.data) {
              setState({
                tairraUserRights: resUserRights.data.data,
                tairraUser: {
                  ...resTairra.data.data,
                },
                accessToken: tairraToken,
                tairraLoading: false,
              });
            } else {
              console.warn('Tairra API did not return valid user rights data:', resUserRights.data);
              setState({ tairraUserRights: null, tairraLoading: false });
            }
          } else {
            console.warn('Tairra API did not return valid data:', resTairra.data);
            setState({
              tairraUser: null,
              tairraUserRights: null,
              accessToken: null,
              tairraLoading: false,
            });
          }
        } else {
          console.error('Employee ID not found in session storage.');
          setState({
            tairraUser: null,
            tairraUserRights: null,
            accessToken: null,
            tairraLoading: false,
          });
        }
      } else {
        setState({
          tairraUser: null,
          tairraUserRights: null,
          accessToken: null,
          tairraLoading: false,
        });
      }
    } catch (error) {
      console.error(error);
      setState({
        user: null,
        tairraUser: null,
        tairraUserRights: null,
        loading: false,
        tairraLoading: false,
      });
    }
  }, [setState]);

  useEffect(() => {
    checkUserSession();
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticatedTairra = state.tairraUser ? 'authenticated' : 'unauthenticated';
  const status = state.loading || state.tairraLoading ? 'loading' : checkAuthenticatedTairra;
  const employeeId = state.tairraUser ? Number(state.tairraUser.id) : null;

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
            ...state.user,
            role: state.user?.role ?? 'admin',
          }
        : null,
      tairraUser: state.tairraUser
        ? {
            ...state.tairraUser,
            role: state.tairraUser?.role ?? 'admin',
          }
        : null,
      tairraUserRights: state.tairraUserRights,
      employeeId, // Expose employeeId explicitly
      checkUserSession,
      accessToken: state.accessToken, // Expose accessToken in context
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),
    [checkUserSession, state.user, state.tairraUser, state.tairraUserRights, employeeId, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
